import { DisconnectButton, TrackToggle } from '@livekit/components-react';
import { Track } from 'livekit-client';

import { canShareScreen } from '../capabilities';
import { format } from '../i18n';
import { useStrings } from '../i18n/context';
import { isListeningMode, type ListeningMode } from '../protocol';

/** The side panels; one is open at a time. */
export type Panel = 'transcript' | 'chat' | 'notes' | 'copilot';

interface Props {
  readonly panel: Panel | null;
  readonly onTogglePanel: (panel: Panel) => void;
  /** The listening mode (ADR 001, the listener rule): voice, subtitles only, or off. */
  readonly mode: ListeningMode;
  readonly onModeChange: (mode: ListeningMode) => void;
  /** Chat entries that arrived while the chat was closed. */
  readonly chatUnread: number;
  /** The host's copilot (B-023); absent for everyone but the host. The button opens the panel
   *  and starts the copilot the first time; later it shows or hides the panel while the copilot
   *  keeps running (stopping is in the panel). */
  readonly copilot?: { readonly running: boolean; readonly onToggle: () => void };
}

/** Microphone, camera, screen share, the listening mode, the panels and leave. Each is a
 *  44 px control with its name from the catalog; the toggles report their state with aria-pressed. */
export function MeetingControls({ panel, onTogglePanel, mode, onModeChange, chatUnread, copilot }: Props) {
  const strings = useStrings();
  return (
    <fieldset className="controls">
      <legend className="visually-hidden">{strings.controlsLabel}</legend>
      <TrackToggle source={Track.Source.Microphone} className="button control">
        {strings.microphone}
      </TrackToggle>
      <TrackToggle source={Track.Source.Camera} className="button control">
        {strings.camera}
      </TrackToggle>
      {canShareScreen() && (
        <TrackToggle source={Track.Source.ScreenShare} className="button control">
          {strings.screenShare}
        </TrackToggle>
      )}
      <label className="control-select">
        <span>{strings.modeLabel}</span>
        <select
          value={mode}
          onChange={(event) => {
            if (isListeningMode(event.target.value)) onModeChange(event.target.value);
          }}
        >
          <option value="voice">{strings.modeVoice}</option>
          <option value="subtitles">{strings.modeSubtitles}</option>
          <option value="off">{strings.modeOff}</option>
        </select>
      </label>
      <button
        type="button"
        className="button control"
        aria-pressed={panel === 'transcript'}
        aria-controls="vm-transcript"
        onClick={() => onTogglePanel('transcript')}
      >
        {strings.transcript}
      </button>
      <button
        type="button"
        className="button control"
        aria-pressed={panel === 'chat'}
        aria-controls="vm-chat"
        onClick={() => onTogglePanel('chat')}
      >
        {strings.chat}
        {chatUnread > 0 && <span className="control__badge">{format(strings.chatUnread, { count: chatUnread })}</span>}
      </button>
      {copilot && (
        <button
          type="button"
          className={copilot.running ? 'button control control--running' : 'button control'}
          aria-pressed={panel === 'copilot'}
          aria-controls="vm-copilot"
          onClick={copilot.onToggle}
        >
          {strings.copilot}
          {copilot.running && <span className="visually-hidden"> ({strings.copilotRunning})</span>}
        </button>
      )}
      <button
        type="button"
        className="button control"
        aria-pressed={panel === 'notes'}
        aria-controls="vm-notes"
        onClick={() => onTogglePanel('notes')}
      >
        {strings.notes}
      </button>
      <DisconnectButton className="button control control--leave">{strings.leave}</DisconnectButton>
    </fieldset>
  );
}
